import React, { useEffect, useState, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { supabase } from './services/supabase';
import { AuthContext, User, Site } from './contexts/AuthContext';
import { setSiteContext, setUserContext, clearContext } from './services/api';
import api from './services/api';
import OneSignal from 'react-onesignal';
import Login from './pages/Login';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import AdminPanel from './pages/AdminPanel';
import PlatformAdmin from './pages/PlatformAdmin';
import ToolHub from './pages/ToolHub';
import RequestAccess from './pages/RequestAccess';
import AcceptInvite from './pages/AcceptInvite';
import PrivacyPolicy from './pages/PrivacyPolicy';
import { IXVerifyApp } from './ixverify';
import { IntelligenceApp } from './intelligence';
import { EregApp } from './ereg';
import { LMSApp } from './lms';
import { OncallApp } from './oncall';
import { StudyOrganizerApp } from './studyorganizer';
import { MHRApp } from './mhr';
import { CRMApp } from './crm';
import { InsightsApp } from './insights/InsightsApp';
import { ContractsApp } from './contracts/ContractsApp';
import BookCallback from './crm/pages/BookCallback';
import PublicForm from './crm/pages/PublicForm';
import PublicSign from './crm/pages/PublicSign';
import MaintenanceScreen from './components/MaintenanceScreen';
import { isMaintenanceActive, onMaintenanceChange, probeMaintenance } from './services/maintenance';
// Import module providers to persist state across navigation
import { StudyOrganizerProvider } from './studyorganizer/contexts';
import { IntelligenceProvider } from './intelligence/contexts';
import { EregProvider } from './ereg/contexts/EregContext';
import { MHRProvider } from './mhr/contexts';
import { CRMProvider } from './crm/contexts';
import { InsightsProvider } from './insights/contexts';
import { ContractsProvider } from './contracts/ContractsProvider';
import { LmsProvider } from './lms/LmsProvider';
import { ThemeProvider } from './theme/ThemeContext';
import './App.css';

// Wrapper component that provides all module contexts
// This ensures state persists when navigating between modules
function ModuleProviders({ children }: { children: React.ReactNode }) {
  return (
    <StudyOrganizerProvider>
      <IntelligenceProvider>
        <EregProvider>
          <MHRProvider>
            <CRMProvider>
              <InsightsProvider>
                <ContractsProvider>
                  <LmsProvider>{children}</LmsProvider>
                </ContractsProvider>
              </InsightsProvider>
            </CRMProvider>
          </MHRProvider>
        </EregProvider>
      </IntelligenceProvider>
    </StudyOrganizerProvider>
  );
}

function App() {
  const [session, setSession] = useState<any>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [needsAccessRequest, setNeedsAccessRequest] = useState(false);
  // Cutover maintenance mode (GCP migration §8). Default false = normal app.
  const [maintenance, setMaintenance] = useState<boolean>(isMaintenanceActive());

  // Site state
  const [currentSite, setCurrentSiteState] = useState<Site | null>(null);
  const [availableSites, setAvailableSites] = useState<Site[]>([]);
  const [sitesLoading, setSitesLoading] = useState(true);

  // Computed properties
  const hasMultipleSites = useMemo(() => availableSites.length > 1, [availableSites]);
  const isSuperAdmin = useMemo(() => user?.role === 'super_admin', [user]);
  const isAdmin = useMemo(() => ['super_admin', 'administrator'].includes(user?.role || ''), [user]);

  // Wrapper for setCurrentSite that also updates localStorage
  const setCurrentSite = (site: Site | null) => {
    setCurrentSiteState(site);
    setSiteContext(site?.id || null);
  };

  // Track current user ID to avoid unnecessary refetches
  const currentUserIdRef = React.useRef<string | null>(null);

  useEffect(() => {
    // Check active session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session?.user) {
        currentUserIdRef.current = session.user.id;
        fetchUserProfile(session.user.id);
      } else {
        currentUserIdRef.current = null;
        setLoading(false);
        setSitesLoading(false);
      }
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      // Only update session reference (for token refresh, etc.)
      setSession(session);

      // Only refetch profile on actual auth changes, not token refreshes
      // This prevents page resets when switching browser tabs
      if (event === 'SIGNED_IN' || event === 'USER_UPDATED') {
        if (session?.user && session.user.id !== currentUserIdRef.current) {
          currentUserIdRef.current = session.user.id;
          setLoading(true);
          fetchUserProfile(session.user.id);
        }
      } else if (event === 'SIGNED_OUT') {
        currentUserIdRef.current = null;
        setUser(null);
        setCurrentSite(null);
        setAvailableSites([]);
        clearContext();
        setNeedsAccessRequest(false);
        setLoading(false);
        setSitesLoading(false);
        // Drop the OneSignal external-id alias so a shared device never keeps
        // receiving the previous user's targeted pushes (ixv2 WP-3.4).
        OneSignal.logout().catch(() => {});
      }
      // Ignore TOKEN_REFRESHED and other events - they don't require refetching
    });

    return () => subscription.unsubscribe();
  }, []);

  // Cutover maintenance mode (GCP migration §8): subscribe to the shared signal
  // (also flipped by the api.ts 503 interceptor) and probe once on boot so the
  // maintenance screen shows even on routes that make no API call (e.g. /login).
  useEffect(() => {
    const unsubscribe = onMaintenanceChange(setMaintenance);
    probeMaintenance();
    return unsubscribe;
  }, []);

  // Fetch sites when user is loaded
  useEffect(() => {
    if (user) {
      fetchUserSites();
    }
  }, [user]);

  // Targeted push (ixv2 WP-3.4, plan §8.3): alias this browser's OneSignal
  // subscription to the PLATFORM user id, so backend sends can target
  // `include_aliases: { external_id: [<user id>] }` (IXVerify notifications,
  // and any future per-user push). Best-effort — push is never load-bearing
  // (the SSE bell + email are the fallback); OneSignal init/network failures
  // must not disturb auth.
  useEffect(() => {
    if (!user?.id) return;
    OneSignal.login(user.id).catch(() => {
      // Non-fatal: unconfigured/blocked OneSignal simply means no push alias.
    });
  }, [user?.id]);

  // Prompt for notification permission after user is fully logged in (once per session)
  useEffect(() => {
    const promptForNotifications = async () => {
      // Only prompt if user is fully authenticated and has sites loaded
      // This ensures we don't prompt on the login page
      if (session && user && !needsAccessRequest && !loading && availableSites.length > 0) {
        // Check if we've already prompted this session
        const hasPromptedThisSession = sessionStorage.getItem('onesignal_prompted');
        if (hasPromptedThisSession) return;

        // Check if user is already subscribed
        const isSubscribed = await OneSignal.User.PushSubscription.optedIn;
        if (isSubscribed) return;

        // Small delay to ensure the user has landed on the tool hub
        setTimeout(() => {
          // Prompt and mark as prompted for this session
          sessionStorage.setItem('onesignal_prompted', 'true');
          OneSignal.Slidedown.promptPush();
        }, 2000);
      }
    };
    promptForNotifications();
  }, [session, user, needsAccessRequest, loading, availableSites]);

  const fetchUserProfile = async (userId: string) => {
    try {
      // Fetch the caller's own profile via the platform API (data decoupled from
      // supabase in WP-3.4). A 404 means no profile row yet → request access.
      let data;
      try {
        const resp = await api.get('/api/users/me');
        data = resp.data?.user;
      } catch (err) {
        const status = (err as { response?: { status?: number } })?.response?.status;
        if (status === 404) {
          // User profile doesn't exist - they need to request access
          setNeedsAccessRequest(true);
          setLoading(false);
          return;
        }
        throw err;
      }

      // Check if user has been soft deleted
      if (data.deleted_at) {
        console.log('User account has been deleted');
        await supabase.auth.signOut();
        setUser(null);
        clearContext();
        setNeedsAccessRequest(true);
        setLoading(false);
        return;
      }

      setUser(data);
      setUserContext(userId, data.email);
      setNeedsAccessRequest(false);
    } catch (error) {
      console.error('Error fetching user profile:', error);
      setNeedsAccessRequest(true);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserSites = async () => {
    if (!user) return;

    setSitesLoading(true);
    try {
      const response = await api.get('/api/sites');
      const sites = response.data;
      setAvailableSites(sites);

      // Set current site from localStorage or default to first available
      const savedSiteId = localStorage.getItem('currentSiteId');
      const savedSite = sites.find((s: Site) => s.id === savedSiteId);

      if (savedSite) {
        setCurrentSite(savedSite);
      } else if (sites.length > 0) {
        setCurrentSite(sites[0]);
      }
    } catch (error) {
      console.error('Error fetching sites:', error);
    } finally {
      setSitesLoading(false);
    }
  };

  // Maintenance takes precedence over everything (all routes, public + private).
  if (maintenance) {
    return <MaintenanceScreen />;
  }

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  const contextValue = {
    session,
    user,
    setUser,
    currentSite,
    setCurrentSite,
    availableSites,
    setAvailableSites,
    hasMultipleSites,
    isSuperAdmin,
    isAdmin,
    sitesLoading,
    setSitesLoading,
  };

  // Helper to determine redirect for authenticated users without profile
  const getAuthenticatedRedirect = (targetPath: string) => {
    if (needsAccessRequest) {
      return <Navigate to="/request-access" />;
    }
    return <Navigate to={targetPath} />;
  };

  return (
    <ThemeProvider>
    <AuthContext.Provider value={contextValue}>
      <ModuleProviders>
        <Router>
          <div className="App">
            <Routes>
            {/* Public routes */}
            <Route
              path="/login"
              element={!session ? <Login /> : getAuthenticatedRedirect("/")}
            />
            <Route
              path="/accept-invite/:token"
              element={<AcceptInvite />}
            />
            <Route
              path="/privacy"
              element={<PrivacyPolicy />}
            />
            <Route
              path="/reset-password"
              element={<ResetPassword />}
            />

            {/* Request access - for authenticated users without profile */}
            <Route
              path="/request-access"
              element={
                session ? (
                  needsAccessRequest ? <RequestAccess /> : <Navigate to="/" />
                ) : (
                  <Navigate to="/login" />
                )
              }
            />

            {/* Protected routes - require session AND user profile */}
            <Route
              path="/"
              element={
                session ? (
                  needsAccessRequest ? <Navigate to="/request-access" /> : <ToolHub />
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/status-board"
              element={
                session ? (
                  needsAccessRequest ? <Navigate to="/request-access" /> : <Dashboard />
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            {/* IXVerify v2 (ixv2 WP-2.3) — module shell behind the build-time
                kill switch REACT_APP_IXVERIFY_ENABLED; while unset the app
                renders the platform-standard tool-unavailable screen */}
            <Route
              path="/ixverify/*"
              element={<IXVerifyApp />}
            />
            {/* Site Intelligence routes - uses its own context */}
            <Route
              path="/intelligence/*"
              element={<IntelligenceApp />}
            />
            {/* eReg routes - uses its own context */}
            <Route
              path="/ereg/*"
              element={<EregApp />}
            />
            {/* LMS routes - Learning Management System */}
            <Route
              path="/lms/*"
              element={<LMSApp />}
            />
            {/* 24-Hour Line routes - dark-launched (feature_key 'oncall');
                entitlement-gated inside OncallApp */}
            <Route
              path="/oncall/*"
              element={<OncallApp />}
            />
            {/* SiteOrganizer routes - Study portal and contact management */}
            <Route
              path="/siteorganizer/*"
              element={<StudyOrganizerApp />}
            />
            {/* Operations Insights routes - admin-only workload visualization */}
            <Route
              path="/insights/*"
              element={<InsightsApp />}
            />
            {/* Budgets and Contracts routes - dark-launched (feature_key
                'contracts'); entitlement-gated inside ContractsApp */}
            <Route
              path="/contracts/*"
              element={<ContractsApp />}
            />
            {/* MHR routes - Medical History Review */}
            <Route
              path="/mhr/*"
              element={<MHRApp />}
            />
            {/* CRM routes - Lead CRM */}
            <Route
              path="/crm/*"
              element={<CRMApp />}
            />
            {/* PUBLIC patient-facing callback booking page (token-authed link
                texted to leads by the CRM — no session, no platform chrome) */}
            <Route
              path="/book/:token"
              element={<BookCallback />}
            />
            {/* PUBLIC patient-facing texted form page (token-authed, same contract) */}
            <Route
              path="/f/:token"
              element={<PublicForm />}
            />
            {/* PUBLIC patient-facing e-sign page (token-authed, same contract) */}
            <Route
              path="/sign/:token"
              element={<PublicSign />}
            />
            <Route
              path="/admin"
              element={
                session && isAdmin && !needsAccessRequest ?
                  <PlatformAdmin /> :
                  <Navigate to="/" />
              }
            />
            <Route
              path="/status-board/admin"
              element={
                session && isAdmin && !needsAccessRequest ?
                  <AdminPanel /> :
                  <Navigate to="/" />
              }
            />
          </Routes>
        </div>
      </Router>
      </ModuleProviders>
    </AuthContext.Provider>
    </ThemeProvider>
  );
}

export default App;
